import { useTranslation } from "utilities/i18n";
import { DISMISS, useNotification } from "collections/Notifications";
import { copyToClipboard } from "utilities/clipboard";
import React, { useCallback } from "react";

export interface ClipboardOptions {
  notify?: boolean;
  notifyName?: string;
  onCopyText?: (text: string, result: boolean) => void;
}

export interface ClipboardAPI {
  copyText: (text: string) => Promise<boolean>;
}

export function useClipboard({
  notify = true,
  notifyName,
  onCopyText
}: ClipboardOptions): ClipboardAPI {
  const { t } = useTranslation();
  const [addNotification] = useNotification();

  const copyText = useCallback(
    async (text: string) => {
      const result = await copyToClipboard(text);
      try {
        if (onCopyText) {
          onCopyText(text, result);
        }
      } finally {
        if (notify) {
          const aNotifyName = notifyName ?? t("DATA");
          addNotification({
            id: result
              ? "copy-to-clipboard-success"
              : "copy-to-clipboard-failed",
            type: result ? "success" : "error",
            message: t(
              result ? "COPIED_TO_CLIPBOARD" : "COPY_TO_CLIPBOARD_FAILED",
              { name: aNotifyName }
            ),
            dismiss: DISMISS.SLOW
          });
        }
      }

      return result;
    },
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notify, notifyName, onCopyText]
  );

  return {
    copyText
  };
}

export interface ClipboardProps extends ClipboardOptions {
  getTextToCopy: () => string;
}

export const Clipboard: React.FC<ClipboardProps> = ({
  getTextToCopy,
  notify = true,
  notifyName,
  onCopyText,
  children,
  ...otherProps
}) => {
  const clipboard = useClipboard({
    notify: notify,
    notifyName,
    onCopyText
  });

  const element = React.Children.only<any>(children);

  const onClickHandler = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();

    const promise = clipboard.copyText(getTextToCopy());
    if (typeof element?.props?.onClick === "function") {
      promise.finally(() => {
        element.props.onClick(e);
      });
    }
  };

  return element
    ? React.cloneElement(element, { ...otherProps, onClick: onClickHandler })
    : null;
};
